import { useEffect, useState, useCallback, useContext } from 'react';
import { Button, Box, Stack, Snackbar, Alert } from '@mui/material';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import ReactGA from 'react-ga';
// Our Components
import { CustomTableContainer } from './table/CustomTableContainer';
import { FilterByDate } from './FilterByDate';
import { FilterByDepartment } from './FilterByDepartment';
import { FilterByLast4 } from './FilterByLast4';
import { FilterByUser } from './FilterByUser';
import { HeaderComponent } from 'components/shared/HeaderComponent';
import { HIDE_ERROR_ALERT, HIDE_SUCCESS_ALERT, SET_CARD_TRANSACTIONS } from 'context/transactions/transactionsTypes';
import { TransactionsContext } from 'context/transactions/transactionsContext';
import ActiveCards from './ActiveCards';
import Amount from './Amount';
import TableTitle from 'components/shared/Tables/TableTitle';
import TransactionTable from './table/TransactionsTable';
// Services
import { Cards, Transactions, Overview, Users } from 'lib/api';
// Utils and Helpers
// Assets
import './Transactions.component.css';

function TransactionsSection() {
    const {
        state: { isSuccessAlertOpen, isErrorAlertOpen, shouldUpdateCardTransactionList },
        dispatch
    } = useContext(TransactionsContext);
    const [datesRange, setDatesRange] = useState([dayjs().subtract(1, 'month'), dayjs().endOf('day')]);
    const [pagination, setPagination] = useState({
        page: 0,
        rowsPerPage: 10
    });
    const [amountSpendInPeriod, setAmountSpendInPeriod] = useState(0);
    const [loading, setLoading] = useState(false);
    const [cardholders, setCardholders] = useState([]);
    const [cards, setCards] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [selectedLast4, setSelectedLast4] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const fetchCards = useCallback(async () => {
        try {
            const queryParams = {
                limit: 100
            };
            const { results } = await Cards.getCards(queryParams);
            setCards(results);
        } catch (error) {
            console.error(error);
        }
    }, []);
    const fetchUsers = useCallback(async () => {
        try {
            const queryParams = {
                limit: 100,
                order: 'DESC'
            };
            const { results } = await Users.getUsers(queryParams);
            setCardholders(results);
        } catch (error) {
            console.error(error);
        }
    }, []);
    const fetchOverviewInfo = useCallback(async () => {
        try {
            const cardStatistics = await Overview.getOverviewInfo();
            setStatistics(cardStatistics);
        } catch (error) {
            console.error(error);
        }
    }, []);
    useEffect(() => {
        fetchCards();
        fetchUsers();
        fetchOverviewInfo();
    }, [fetchCards, fetchOverviewInfo, fetchUsers]);

    const fetchCardTransactions = useCallback(async () => {
        setLoading(true);
        const last4 = selectedLast4.map(last4 => last4.solidData.last4);
        const users = selectedUsers.map(user => user.id);
        const departments = selectedDepartments.map(department => department.department);
        try {
            const [startDate, endDate] = datesRange;
            const queryParams = {
                startDate: dayjs(startDate).startOf('day').toISOString(),
                endDate: dayjs(endDate).endOf('day').toISOString(),
                last4: last4.join(),
                users: users.join(),
                departments: departments.join(),
                page: pagination.page,
                limit: pagination.rowsPerPage
            };
            const cardTransactionsResult = await Transactions.getCardTransactions(queryParams);
            dispatch({
                type: SET_CARD_TRANSACTIONS,
                payload: cardTransactionsResult
            });
            setAmountSpendInPeriod(cardTransactionsResult.totalSpent);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    }, [datesRange, dispatch, pagination.page, pagination.rowsPerPage, selectedDepartments, selectedLast4, selectedUsers]);
    useEffect(() => {
        if (
            shouldUpdateCardTransactionList ||
            datesRange ||
            pagination.page ||
            pagination.rowsPerPage ||
            selectedDepartments ||
            selectedLast4 ||
            selectedUsers
        ) {
            fetchCardTransactions();
        }
    }, [
        datesRange,
        fetchCardTransactions,
        pagination.page,
        pagination.rowsPerPage,
        selectedDepartments,
        selectedLast4,
        selectedUsers,
        shouldUpdateCardTransactionList
    ]);

    const handleExportCSVClick = async () => {
        ReactGA.event({
            category: 'Transacions',
            action: 'Export Transactions',
            label: 'Transactions'
        });
        setLoading(true);
        try {
            const [startDate, endDate] = datesRange;
            const selected4 = selectedLast4.map(last4 => last4.solidData.last4);
            const users = selectedUsers.map(user => user.id);
            const departments = selectedDepartments.map(department => department.department);
            const queryParams = {
                startDate: dayjs(startDate).startOf('day').toISOString(),
                endDate: dayjs(endDate).endOf('day').toISOString(),
                last4: selected4.join(),
                users: users.join(),
                departments: departments.join()
            };
            const response = await Transactions.fetchTransactionCSVByDate(queryParams);
            const blob = new Blob(['\ufeff', response]);
            saveAs(blob, `CardTransactions-${startDate}-${endDate}.csv`);
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    return (
        <>
            <Stack style={{ minHeight: '86vh' }} sx={{ maxWidth: '95vw' }}>
                <HeaderComponent title="Transactions" />
                <Box className="transactions-titles">
                    <Amount amount={amountSpendInPeriod} />
                    <ActiveCards activeCards={statistics.activeCards} />
                </Box>
                <CustomTableContainer height="600px">
                    <Box
                        sx={{
                            display: {
                                xs: 'grid',
                                md: 'flex'
                            },
                            gridTemplateRows: {
                                xs: 'auto auto'
                            },
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                        <TableTitle>All Transactions</TableTitle>
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', columnGap: '1em' }}>
                            <Button variant="outlined" disabled={loading} onClick={handleExportCSVClick}>
                                Export CSV
                            </Button>
                        </Box>
                    </Box>
                    <Box className="transactions-search">
                        <Box sx={{ display: 'flex', width: '100%', flexFlow: { xs: 'column', lg: 'row' }, flexWrap: 'wrap' }}>
                            <FilterByDate datesRange={datesRange} setDatesRange={setDatesRange} setPagination={setPagination} />

                            <FilterByLast4
                                cards={cards}
                                selectedLast4={selectedLast4}
                                setSelectedLast4={setSelectedLast4}
                                setPagination={setPagination}
                            />
                            <FilterByDepartment
                                cardholders={cardholders}
                                selectedDepartments={selectedDepartments}
                                setSelectedDepartments={setSelectedDepartments}
                                setPagination={setPagination}
                            />
                            <FilterByUser
                                cardholders={cardholders}
                                selectedUsers={selectedUsers}
                                setSelectedUsers={setSelectedUsers}
                                setPagination={setPagination}
                            />
                        </Box>
                    </Box>
                    <TransactionTable pagination={pagination} setPagination={setPagination} />
                </CustomTableContainer>
            </Stack>

            <Snackbar
                sx={{ top: '79px' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={isSuccessAlertOpen}
                onClose={() => dispatch({ type: HIDE_SUCCESS_ALERT })}
                autoHideDuration={3000}>
                <Alert className="defi-snackbar-alert" severity="success" onClose={() => dispatch({ type: HIDE_SUCCESS_ALERT })}>
                    Transaction details updated successfully.
                </Alert>
            </Snackbar>
            <Snackbar
                sx={{ top: '79px' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={isErrorAlertOpen}
                onClose={() => dispatch({ type: HIDE_ERROR_ALERT })}
                autoHideDuration={3000}>
                <Alert className="defi-snackbar-alert" severity="error" onClose={() => dispatch({ type: HIDE_ERROR_ALERT })}>
                    Transaction details were not saved. Please try again.
                </Alert>
            </Snackbar>
        </>
    );
}

export default TransactionsSection;
